import type {
  ActionType,
  AgeGroup,
  AppointmentRole,
  CountedNoun,
  GuardianRelationship,
  KitDesign,
  KitRank,
  MatchKitChoice,
  MatchStatus,
  PlayerFoot,
  PlayerPosition,
  PositionLabel,
  Sex,
  SubscriptionStatus,
  TeamGender,
  TeamSheetStaffRole,
} from '../en/domain.ts'

// Italian labels. FIGC categories look untranslated because the value IS the Italian name.

export const IT_ACTION_TYPES: Record<ActionType, string> = {
  goal: 'Gol',
  own_goal: 'Autogol',
  assist: 'Assist',
  shot: 'Tiro',
  save: 'Parata',
  dribble: 'Dribbling',
  tackle: 'Contrasto',
  key_pass: 'Passaggio chiave',
  recovery: 'Recupero',
  yellow_card: 'Ammonizione',
  red_card: 'Espulsione',
  substitution: 'Sostituzione',
}

export const IT_APPOINTMENT_ROLES: Record<AppointmentRole, string> = {
  official: 'Dirigente di società',
  team_official: 'Dirigente di squadra',
  coach: 'Allenatore',
}

export const IT_PLAYER_POSITIONS: Record<PlayerPosition, PositionLabel> = {
  goalkeeper: { one: 'Portiere', many: 'Portieri', short: 'Por' },
  defender: { one: 'Difensore', many: 'Difensori', short: 'Dif' },
  midfielder: { one: 'Centrocampista', many: 'Centrocampisti', short: 'Cen' },
  forward: { one: 'Attaccante', many: 'Attaccanti', short: 'Att' },
}

export const IT_PLAYER_FEET: Record<PlayerFoot, string> = {
  left: 'Sinistro',
  right: 'Destro',
  both: 'Ambidestro',
}

export const IT_SEXES: Record<Sex, string> = {
  male: 'Maschio',
  female: 'Femmina',
}

export const IT_MATCH_STATUSES: Record<MatchStatus, string> = {
  scheduled: 'In programma',
  played: 'Giocata',
  postponed: 'Rinviata',
}

export const IT_SUBSCRIPTION_STATUSES: Record<SubscriptionStatus, string> = {
  incomplete: 'In attesa di pagamento',
  incomplete_expired: 'Pagamento non completato',
  trialing: 'Piano attivo',
  active: 'Piano attivo',
  past_due: 'Da aggiornare',
  canceled: 'Terminato',
  unpaid: 'Non pagato',
  paused: 'In pausa',
}

export const IT_AGE_GROUPS: Record<AgeGroup, string> = {
  seniores: 'Seniores',
  juniores: 'Juniores',
  allievi: 'Allievi',
  giovanissimi: 'Giovanissimi',
  esordienti: 'Esordienti',
  pulcini: 'Pulcini',
}

export const IT_TEAM_GENDERS: Record<TeamGender, string> = {
  male: 'Maschile',
  female: 'Femminile',
  open: 'Mista',
}

export const IT_SQUAD_PLAYER_NOUNS: Record<TeamGender, CountedNoun> = {
  male: { one: 'giocatore', many: 'giocatori' },
  female: { one: 'giocatrice', many: 'giocatrici' },
  open: { one: 'giocatore', many: 'giocatori' },
}

export const IT_SQUAD_COACH_NOUN: CountedNoun = { one: 'allenatore', many: 'allenatori' }

export const IT_GUARDIAN_RELATIONSHIPS: Record<GuardianRelationship, string> = {
  mother: 'Madre',
  father: 'Padre',
  guardian: 'Tutore',
}

export const IT_KIT_RANKS: Record<KitRank, string> = {
  first: 'Prima divisa',
  second: 'Seconda divisa',
}

/** La divisa di gara: the società's kits by their plain name, the squad's saying whose they are
 *  (the app composes «della squadra» and «della società», the catalog has no such word). */
export const IT_MATCH_KIT_CHOICES: Record<MatchKitChoice, string> = {
  ...IT_KIT_RANKS,
  team_first: 'Prima divisa della squadra',
  team_second: 'Seconda divisa della squadra',
  other: 'Altri colori',
}

/** The società's kits when a squad's stand beside them, or in a summary: they say whose they are. */
export const IT_CLUB_KIT_NAMES: Record<KitRank, string> = {
  first: 'Prima divisa della società',
  second: 'Seconda divisa della società',
}

export const IT_TEAM_SHEET_STAFF_ROLES: Record<TeamSheetStaffRole, string> = {
  coach: 'Allenatore',
  accompanying_official: 'Dirigente accompagnatore',
  referee_liaison_official: 'Dirigente addetto agli arbitri',
  doctor: 'Medico',
  masseur: 'Massaggiatore',
  assistant_referee: 'Assistente arbitro',
}

export const IT_KIT_DESIGNS: Record<KitDesign, string> = {
  solid: 'Tinta unita',
  vertical_stripes: 'Righe verticali',
  horizontal_stripes: 'Righe orizzontali',
  diagonal_stripes: 'Righe diagonali',
  halves: 'Metà e metà',
}

export const IT_UNKNOWN_VALUE = 'Non specificato'
